import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs';
import { of } from 'rxjs';
import { ProductsService } from '../../../services/products.service';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { Product } from '../../interfaces';

@Component({
  selector: 'app-product-lookup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './product-lookup.html',
  styleUrl: './product-lookup.css',
})
export class ProductLookup extends BaseAuthComponent implements OnInit, OnChanges {
  @Input() label = 'Producto';
  @Input() placeholder = 'Busca por nombre, SKU o categoría';
  @Input() selectedProduct: Product | null = null;
  @Output() productSelected = new EventEmitter<Product>();
  @Output() productCleared = new EventEmitter<void>();

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown', { static: false }) dropdown?: ElementRef<HTMLDivElement>;

  private productsService = inject(ProductsService);
  private fb = inject(FormBuilder);

  searchForm = this.fb.group({
    searchTerm: [''],
  });

  get searchTermControl(): FormControl<string | null> {
    return this.searchForm.get('searchTerm') as FormControl<string | null>;
  }

  searchResults = signal<Product[]>([]);
  isSearching = signal(false);
  isDropdownOpen = signal(false);
  selectedProductSignal = signal<Product | null>(null);

  constructor() {
    super();
    this.setupSearch();
  }

  ngOnInit(): void {
    if (this.selectedProduct) {
      this.selectedProductSignal.set(this.selectedProduct);
      this.updateInputValue();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProduct']) {
      if (this.selectedProduct) {
        this.selectedProductSignal.set(this.selectedProduct);
        this.updateInputValue();
      } else {
        this.selectedProductSignal.set(null);
        this.searchForm.patchValue({ searchTerm: '' });
      }
    }
  }

  private setupSearch(): void {
    this.searchForm
      .get('searchTerm')
      ?.valueChanges.pipe(
        debounceTime(250),
        map((v) => (v ?? '').trim()),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < 2) {
            this.isSearching.set(false);
            this.searchResults.set([]);
            return of([]);
          }
          this.isSearching.set(true);
          this.isDropdownOpen.set(true);
          return this.productsService
            .getProducts({
              searchTerm: q,
              isActive: true,
              pageSize: 20,
            })
            .pipe(
              map((response) => response.products || []),
              catchError(() => {
                this.isSearching.set(false);
                return of([]);
              })
            );
        })
      )
      .subscribe({
        next: (results) => {
          this.searchResults.set(results);
          this.isSearching.set(false);
        },
        error: () => {
          this.isSearching.set(false);
          this.searchResults.set([]);
        },
      });
  }

  private updateInputValue(): void {
    const product = this.selectedProductSignal();
    if (product) {
      this.searchForm.patchValue({ searchTerm: product.name }, { emitEvent: false });
    }
  }

  onSearchFocus(): void {
    const searchTerm = this.searchForm.get('searchTerm')?.value?.trim() || '';
    if (searchTerm.length >= 2 && this.searchResults().length > 0) {
      this.isDropdownOpen.set(true);
    }
  }

  onSearchInput(): void {
    const searchTerm = this.searchForm.get('searchTerm')?.value?.trim() || '';
    // If user clears the input, clear selection
    if (searchTerm.length === 0) {
      this.clearProduct();
    } else if (searchTerm.length >= 2) {
      this.isDropdownOpen.set(true);
    } else {
      this.isDropdownOpen.set(false);
    }
  }

  selectProduct(product: Product): void {
    this.selectedProductSignal.set(product);
    this.searchForm.patchValue({ searchTerm: product.name }, { emitEvent: false });
    this.isDropdownOpen.set(false);
    this.searchResults.set([]);
    this.productSelected.emit(product);
  }

  clearProduct(): void {
    this.selectedProductSignal.set(null);
    this.searchForm.patchValue({ searchTerm: '' });
    this.isDropdownOpen.set(false);
    this.searchResults.set([]);
    this.productCleared.emit();
  }

  getProductDisplayName(product: Product | null): string {
    if (!product) return '';
    return product.name || product.sku || '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.dropdown?.nativeElement &&
      !this.dropdown.nativeElement.contains(event.target as Node) &&
      this.searchInput?.nativeElement &&
      !this.searchInput.nativeElement.contains(event.target as Node)
    ) {
      this.isDropdownOpen.set(false);
    }
  }
}

